"use client";

import { useMutation, useQuery } from "@apollo/client";
import { Check, Crown, GripVertical, Plus, Sprout, Star, Wrench, X } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useMemo, useState, type HTMLAttributes } from "react";
import toast from "react-hot-toast";
import { DragDropContext, Draggable, Droppable, type DropResult } from "react-beautiful-dnd";
import NewEndorsement from "components/Modal/Endorsement/NewEndorsement";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { gqlEndorsement, gqlSkill, gqlUser } from "gql";
import type { IGetUserEndorsementResultsInput, IGetUserEndorsementsResponse } from "gql/Endorsement/queries";
import type { IUpdateProfileSkillsInput } from "gql/User/mutations";
import {
  Avatar,
  LiquidPill,
  LiquidPillExpandButton,
  LiquidPillGroup,
  type LiquidPillGroupItem,
  Modal,
  PageEmptyStateV2,
  PageLoadingStateV2,
  TagPickerV2,
} from "components/ui/v2";
import { forceRefetchQueries } from "settings/apollo";
import type { IUserEndorsementResult } from "types/Endorsement.type";
import type { ISkill } from "types/Skills.type";
import { getQueryOperator } from "utils/helpers";
import { ResumeTabHeaderV2 } from "./ResumeTabHeaderV2";

const MAX_SKILLS = 10;

type SkillOption = { label: string; value: string };

function SkillRatingV2({ value, disabled, onChange }: { value: number; disabled?: boolean; onChange: (value: number) => void }) {
  const { t } = useTranslation("common");
  const [rating, setRating] = useState(String(value || 0));
  useEffect(() => setRating(String(value || 0)), [value]);

  const levels = [
    { value: "1", labelKey: "Junior", variant: "slate", icon: <Sprout className="h-3.5 w-3.5" /> },
    { value: "2", labelKey: "Semi-Senior", variant: "sky", icon: <Star className="h-3.5 w-3.5" /> },
    { value: "3", labelKey: "Senior", variant: "indigo", icon: <Wrench className="h-3.5 w-3.5" /> },
    { value: "4", labelKey: "Expert", variant: "emerald", icon: <Crown className="h-3.5 w-3.5" /> },
  ] satisfies Array<LiquidPillGroupItem & { labelKey: string }>;

  const handleChange = (nextValue: string) => {
    if (disabled) return;
    const next = rating === nextValue && nextValue === "1" ? "0" : nextValue;
    setRating(next);
    onChange(Number(next));
  };

  return (
    <div className="shrink-0 rounded-full border border-slate-200/80 bg-white/80 p-1 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-zinc-800/70 dark:bg-zinc-950/70">
      <LiquidPillGroup
        items={levels.map(({ labelKey, ...level }) => ({ ...level, label: t(labelKey) }))}
        activeValue={rating}
        onChange={handleChange}
        size="sm"
      />
    </div>
  );
}

function SkillCardV2({
  row,
  currentUserId,
  isOwnProfile,
  onRate,
  onRequest,
  loading,
  dragHandleProps,
}: {
  row: IUserEndorsementResult;
  currentUserId?: string;
  isOwnProfile: boolean;
  onRate: (rating: number, skill: ISkill) => void;
  onRequest: (skill: ISkill) => void;
  loading: boolean;
  dragHandleProps?: HTMLAttributes<HTMLDivElement> | null;
}) {
  const { t } = useTranslation("common");
  const average = row.skill.average || 0;
  const endorsers = row.userEndorsements ?? [];
  const visibleEndorsers = endorsers.slice(0, 8);
  const hiddenEndorsers = Math.max(0, endorsers.length - visibleEndorsers.length);
  const alreadyValidatedByCurrentUser = Boolean(
    currentUserId && endorsers.some((endorsement: any) => endorsement.endorserUser?.id === currentUserId)
  );
  const canValidate = !isOwnProfile && !alreadyValidatedByCurrentUser;

  return (
    <div className="grid grid-cols-[auto_10rem_minmax(0,auto)_8rem] items-center justify-center gap-3 overflow-x-auto py-1">
      {isOwnProfile ? (
        <div
          {...(dragHandleProps ?? {})}
          className="cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing dark:text-zinc-500 dark:hover:text-zinc-300"
          title={t("Arrastra para reordenar")}
          aria-label={t("Arrastra para reordenar")}
        >
          <GripVertical className="h-4 w-4" />
        </div>
      ) : (
        <span className="w-4" />
      )}

      <h3 className="truncate text-right text-base font-semibold leading-tight text-slate-950 dark:text-white">
        {row.skill.name}
      </h3>

      <div className="justify-self-center">
        {isOwnProfile ? (
          <SkillRatingV2 value={average} disabled={loading} onChange={(rating) => onRate(rating, row.skill)} />
        ) : null}
      </div>

      {endorsers.length > 0 || canValidate ? (
        <div className="justify-self-start">
          <LiquidPill
            as="span"
            variant="emerald"
            appearance="tinted"
            shadow="neutral"
            size="xs"
            text={
              <span className="flex items-center gap-1.5">
                {endorsers.length > 0 ? (
                  <span className="flex items-center -space-x-1.5">
                    {visibleEndorsers.map((endorsement: any) => {
                      const endorser = endorsement.endorserUser;
                      const name =
                        [endorser?.name, endorser?.lastname].filter(Boolean).join(" ") ||
                        t("Usuario");
                      return (
                        <span
                          key={endorser?.id ?? name}
                          className="rounded-full ring-2 ring-white dark:ring-zinc-950"
                        >
                          <Avatar src={endorser?.profileImage} name={name} size="xs" />
                        </span>
                      );
                    })}
                    {hiddenEndorsers > 0 ? (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-slate-100 px-1 text-[9px] font-semibold text-slate-600 ring-2 ring-white dark:bg-zinc-800 dark:text-zinc-300 dark:ring-zinc-950">
                        +{hiddenEndorsers}
                      </span>
                    ) : null}
                  </span>
                ) : null}
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm dark:bg-emerald-400">
                  <Check className="h-2.5 w-2.5" />
                </span>
                {canValidate ? (
                  <LiquidPill
                    as="button"
                    type="button"
                    variant="emerald"
                    appearance="tinted"
                    shape="circle"
                    size="xxs"
                    icon={<Check className="h-3 w-3" />}
                    onClick={() => onRequest(row.skill)}
                    aria-label={t("Validar")}
                  />
                ) : null}
              </span>
            }
          />
        </div>
      ) : null}
    </div>
  );
}

export function ResumeSkillsTabV2() {
  const { t } = useTranslation("common");
  const { user } = useUserStoreContext((store) => ({ user: store.user }));
  const [skillsList, setSkillsList] = useState<SkillOption[]>([]);
  const [orderedEndorsements, setOrderedEndorsements] = useState<IUserEndorsementResult[]>([]);
  const [requestSkill, setRequestSkill] = useState<ISkill | null>(null);

  const { data: skillsData, loading: skillsLoading } = useQuery(gqlSkill.queries.GET_USERS_SKILLS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const { data: { skillsWithCategory } = {} } = useQuery(gqlSkill.queries.SKILLS_WITH_CATEGORY, {
    fetchPolicy: "network-only",
    onError: (error) => toast.error(error.message),
  });
  const { data: endorsementData, loading: endorsementsLoading } = useQuery<
    IGetUserEndorsementsResponse,
    { getUserEndorsementResultsInput: IGetUserEndorsementResultsInput }
  >(gqlEndorsement.queries.GET_USER_ENDORSEMENTS_RESULTS, {
    variables: { getUserEndorsementResultsInput: { userId: user?.id || "" } },
    fetchPolicy: "network-only",
    skip: !user?.id,
  });

  useEffect(() => {
    // Prefer endorsement list order (backend restores user.skills order).
    // getUserSkills does not reliably return priority / array order.
    if ((endorsementData?.userEndorsements || []).length > 0) return;

    const rows = (skillsData?.getUserSkills || []).slice(0, MAX_SKILLS);
    setSkillsList(rows.map((row) => ({ label: row.name || "", value: row.id })));
  }, [skillsData, endorsementData]);

  useEffect(() => {
    // API restores order from user.skills array. Do NOT sort by skill.priority here:
    // endorsed skills come from the catalog and often lack the user's priority, which
    // would scramble the list (e.g. alphabetical among missing priorities).
    const skillsById = new Map<string, ISkill>(
      (skillsData?.getUserSkills || []).map((skill: ISkill) => [skill.id, skill])
    );
    const rows = (endorsementData?.userEndorsements || [])
      .slice(0, MAX_SKILLS)
      .map((row, index) => {
        const stored = skillsById.get(row.skill.id);
        return {
          ...row,
          skill: {
            ...row.skill,
            priority: stored?.priority ?? row.skill.priority ?? index,
            average: stored?.average ?? row.skill.average,
          },
        };
      });
    setOrderedEndorsements(rows);
  }, [endorsementData, skillsData]);

  // Keep picker pills aligned with card order whenever endorsements refresh.
  useEffect(() => {
    const rows = endorsementData?.userEndorsements || [];
    if (!rows.length) return;
    setSkillsList(
      rows.slice(0, MAX_SKILLS).map((row) => ({
        value: row.skill.id,
        label: row.skill.name || row.skill.id,
      }))
    );
  }, [endorsementData]);

  const [updateProfile, { loading: updateProfileLoading }] = useMutation(
    gqlUser.mutations.UPDATE_USER_PROFILE,
    {
      notifyOnNetworkStatusChange: true,
      refetchQueries: [
        getQueryOperator(gqlUser.queries.GET_FULL_USER),
        getQueryOperator(gqlEndorsement.queries.GET_USER_ENDORSEMENTS_RESULTS),
        getQueryOperator(gqlUser.queries.USER_LEVEL),
        getQueryOperator(gqlSkill.queries.GET_USERS_SKILLS),
      ],
      onCompleted: () => toast.success(t("Habilidades guardadas correctamente")),
      onError: ({ message }) => toast.error(message),
    }
  );

  const [updateSkillRating, { loading: updateRatingLoading }] = useMutation<{
    updateProfileSkillsInput: IUpdateProfileSkillsInput;
  }>(gqlUser.mutations.UPDATE_PROFILE_SKILLS, {
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      getQueryOperator(gqlUser.queries.GET_FULL_USER),
      getQueryOperator(gqlEndorsement.queries.GET_USER_ENDORSEMENTS_RESULTS),
      getQueryOperator(gqlUser.queries.USER_LEVEL),
      getQueryOperator(gqlSkill.queries.GET_USERS_SKILLS),
    ],
  });

  const options = useMemo(
    () => skillsWithCategory?.map((skill: ISkill) => ({ value: skill.id, label: skill.name })) || [],
    [skillsWithCategory]
  );

  const atMaxSkills = skillsList.length >= MAX_SKILLS;

  const applySkillsSelection = (values: string[]) => {
    if (values.length > MAX_SKILLS) {
      toast.error(t("Solo puedes seleccionar hasta 10 habilidades"));
    }
    const limited = values.slice(0, MAX_SKILLS);
    // Keep previous order for skills that remain; append new ones at the end.
    const previousOrder = skillsList.map((item) => item.value);
    const kept = previousOrder.filter((id) => limited.includes(id));
    const added = limited.filter((id) => !kept.includes(id));
    const nextIds = [...kept, ...added].slice(0, MAX_SKILLS);
    setSkillsList(
      nextIds.map((value) => ({
        value,
        label: options.find((option: SkillOption) => option.value === value)?.label || value,
      }))
    );
  };

  const handleSaveSkills = async () => {
    const skills = skillsList
      .slice(0, MAX_SKILLS)
      .map((skill, index) => ({
        name: skill.label,
        average: 0,
        id: skill.value,
        primaryOrSecondary: 3,
        priority: index,
      }))
      .filter((skill) => skillsWithCategory?.some((s: ISkill) => s.name === skill.name));
    await updateProfile({ variables: { updateProfileInput: { skills } } });
    forceRefetchQueries([gqlUser.queries.GET_FULL_USER]);
  };

  const handleRateSkill = (rating: number, skill: ISkill) => {
    const nextRating = rating === 1 && skill.average === 1 ? 0 : rating;
    toast.promise(
      updateSkillRating({
        variables: {
          updateProfileSkillsInput: {
            skill: { name: "", average: nextRating, id: skill.id },
          },
        },
      }),
      {
        loading: t("Actualizando habilidades"),
        success: t("Habilidades actualizadas con éxito"),
        error: t("Algo salió mal"),
      }
    );
  };

  const handleChangePriority = async (nextRows: IUserEndorsementResult[]) => {
    // Backend changeFormatSkills sets priority = array index for every skill,
    // so every item shifts (e.g. moving 5→2 pushes old 2..4 down by one).
    const skills = nextRows.map((endorsement, index) => ({
      id: endorsement.skill.id,
      average: endorsement.skill.average,
      name: endorsement.skill.name,
      priority: index,
    }));

    toast.promise(
      updateSkillRating({
        variables: {
          updateProfileSkillsInput: { skills },
        },
      }),
      {
        loading: t("Actualizando habilidades"),
        success: t("Habilidades actualizadas con éxito"),
        error: t("Algo salió mal"),
      }
    );
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const reordered = Array.from(orderedEndorsements);
    const [removed] = reordered.splice(source.index, 1);
    if (!removed) return;
    reordered.splice(destination.index, 0, removed);

    // Reindex priorities locally so UI and pills stay aligned before refetch.
    const next = reordered.map((row, index) => ({
      ...row,
      skill: { ...row.skill, priority: index },
    }));

    setOrderedEndorsements(next);
    setSkillsList(
      next.map((row) => ({
        value: row.skill.id,
        label: row.skill.name || row.skill.id,
      }))
    );
    void handleChangePriority(next);
  };

  return (
    <div className="grid gap-4">
      <ResumeTabHeaderV2
        title={t("Habilidades")}
        description={t("Aquí encontrarás las habilidades que tienes actualmente")}
        action={
          <LiquidPillExpandButton
            icon={<Plus className="h-4 w-4" />}
            label={updateProfileLoading ? t("Guardando") : t("Agregar habilidades")}
            variant="violet"
            size="sm"
            expandTo="left"
            disabled={updateProfileLoading}
            onClick={handleSaveSkills}
            aria-label={t("Agregar habilidades")}
          />
        }
      />

      {skillsList.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {skillsList.map((skill) => (
            <LiquidPill
              key={skill.value}
              as="button"
              type="button"
              variant="violet"
              appearance="tinted"
              shadow="neutral"
              size="xs"
              text={skill.label}
              icon={<X className="h-3 w-3" />}
              onClick={() =>
                setSkillsList((current) => current.filter((item) => item.value !== skill.value))
              }
              aria-label={t("Eliminar habilidad")}
            />
          ))}
        </div>
      ) : null}

      <div className="grid gap-2">
        <TagPickerV2
          data={options}
          value={skillsList.map((row) => row.value)}
          onChange={(values) => applySkillsSelection(values)}
          creatable={false}
          placeholder={
            atMaxSkills
              ? t("Máximo 10 habilidades seleccionadas")
              : t("SELECT_SKILL_PLACEHOLDER")
          }
          size="md"
          variant="violet"
          loading={skillsLoading}
        />
        <div className="flex items-center justify-between px-0.5 text-[9px] text-slate-500 dark:text-zinc-400">
          <span>
            {skillsList.length} / {MAX_SKILLS} {t("seleccionados")}
          </span>
          {atMaxSkills ? (
            <span className="font-medium text-violet-600 dark:text-violet-300">
              {t("Límite alcanzado")}
            </span>
          ) : null}
        </div>
      </div>

      {endorsementsLoading ? (
        <PageLoadingStateV2 title={t("Cargando")} description={t("Habilidades")} />
      ) : orderedEndorsements.length === 0 ? (
        <PageEmptyStateV2
          variant="empty"
          icon={<Star className="h-8 w-8" />}
          title={t("No tienes habilidades registradas")}
          description={t(
            "Agrega tus habilidades y pide validaciones de tus colegas para mostrar tu experiencia"
          )}
        />
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="profile-skills" type="ROW">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`flex flex-col gap-4 rounded-xl transition-colors ${
                  snapshot.isDraggingOver
                    ? "bg-violet-50/60 dark:bg-violet-950/20"
                    : ""
                }`}
              >
                {orderedEndorsements.map((row, index) => (
                  <Draggable key={row.skill.id} draggableId={row.skill.id} index={index}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={
                          dragSnapshot.isDragging
                            ? "rounded-xl bg-white shadow-md dark:bg-zinc-900"
                            : undefined
                        }
                      >
                        <SkillCardV2
                          row={row}
                          currentUserId={user?.id}
                          isOwnProfile={true}
                          onRate={handleRateSkill}
                          onRequest={setRequestSkill}
                          loading={updateRatingLoading}
                          dragHandleProps={dragProvided.dragHandleProps || undefined}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <Modal
        open={!!requestSkill}
        onClose={() => setRequestSkill(null)}
        title={t("Validar habilidad")}
        description={requestSkill?.name}
        size="md"
        panelClassName="max-h-[min(88vh,680px)]"
      >
        {requestSkill ? (
          <NewEndorsement
            skillId={requestSkill.id}
            userId={user?.id || ""}
            name={requestSkill.name || ""}
          />
        ) : null}
      </Modal>
    </div>
  );
}
